/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import UserSegmentToggle from "../common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../../context/UserSegmentContext";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import HeartRateSessionSummaryCards from "./HeartRateSessionSummaryCards";
import HeartRateSessionTrendChart from "./HeartRateSessionTrendChart";
import HeartRateTopTagsPanel from "./HeartRateTopTagsPanel";
import "../active-users/active-users.css";
import "./HeartRateSessions.css";

const LIMIT = 20;
const STATE_KEY = "heartRateSessionsListState";
const EXPORT_COLUMNS = [
  { key: 'row_no', label: 'No' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'country', label: 'Country' },
  { key: 'mobile_app_version', label: 'App Version' },
  { key: 'subscription_status', label: 'Subscription' },
  { key: 'session_count', label: 'Session Count' },
  { key: 'total_tag_used', label: 'Total tag used' },
  { key: 'last_session', label: 'Last Session' },
];

// Format a "YYYY-MM-DD HH:MM:SS" (or ISO) timestamp to dd/MM/yyyy HH:MM:SS,
// matching the legacy on-screen "Last Session" rendering.
const timestampFormatter = (timestamp) => {
  if (!timestamp) return '-';
  const match = String(timestamp).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hours, minutes, seconds] = match;
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '-';
  const p = (v) => String(v).padStart(2, '0');
  return `${p(date.getDate())}/${p(date.getMonth() + 1)}/${date.getFullYear()} ${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
};

const UserHeartRateSessionsCount = () => {
  const navigate = useNavigate();
  const { segment } = useUserSegment();                       // global watch/without/all (feature 023)
  const userSegment = watchFilterValue(segment) || 'all';     // 'with_watch' | 'without_watch' | 'all'

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [users, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalUserBase, setTotalUserBase] = useState(0);
  const [subscribedUserCount, setSubscribedUserCount] = useState(0);
  const [topTags, setTopTags] = useState([]);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const [summary, setSummary] = useState({ current: {}, prior: {}, deltas: {} });
  const [trend, setTrend] = useState([]);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // Restore list state on return from a session drill-down.
    let restored = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
      if (saved) {
        restored = true;
        setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
        setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : null);
        setDisplayToDate(saved.toDate ? new Date(saved.toDate) : null);
        setPreset(saved.preset || null);
        setSearchValue(saved.searchValue || ''); setSubscriptionFilter(saved.subscriptionFilter || '');
        setSortField(saved.sortField || ''); setSortOrder(saved.sortOrder || ''); setPage(saved.page || 1);
      }
    } catch { /* ignore */ }
    if (!restored) {
      // Default to the last 30 days (matches /user-monitor-sessions) so the date
      // filter is always a concrete window, not a silent all-time query.
      const { fromStr, toStr } = presetRange('last30', new Date());
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFromDate(new Date(fromStr)); setDisplayToDate(new Date(toStr));
      setPreset('last30');
    }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  }, [token]);

  // Re-fetch when the global segment changes (skip initial mount).
  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token) return;
    setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  }, [segment]);

  useEffect(() => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({
      fromDate, toDate, preset, searchValue, subscriptionFilter, sortField, sortOrder, page,
    }));
  }, [fromDate, toDate, preset, searchValue, subscriptionFilter, sortField, sortOrder, page]);

  const qs = (p, f, t, s, sf, so, sub) =>
    `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${s}&sortField=${sf}&sortOrder=${so}&subscription_status=${sub}&user_segment=${userSegment}`;
  const rangeQs = (f, t, s, sub) =>
    `from_date=${f}&to_date=${t}&search=${s}&subscription_status=${sub}&user_segment=${userSegment}`;
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const applyList = (d) => {
    setData(d.data || []);
    setTotalItem(d.totalItem || 0);
    setTotalSessions(d.totalSessions || 0);
    setTotalUsers(d.userCount?.[0]?.user_count || 0);
    setTotalUserBase(d.totalUserCount || 0);
    setSubscribedUserCount(d.subscribedUserCount || 0);
    setTopTags(d.topTags || []);
  };
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const getList = async (p, f, t, s, sf, so, sub) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/user-heart-rate-sessions?${qs(p, f, t, s, sf, so, sub)}`, auth());
      applyList(res.data);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, s, sf, so, sub) => {
    setLoading(true); setError('');
    const range = rangeQs(f, t, s, sub);
    try {
      const [list, sum, tr] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/user-heart-rate-sessions?${qs(p, f, t, s, sf, so, sub)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/heart-rate-session-summary?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/heart-rate-session-trend?${range}`, auth()),
      ]);
      applyList(list.data);
      const s2 = sum?.data?.data;
      setSummary(s2 && s2.current ? s2 : { current: {}, prior: {}, deltas: {} });
      setTrend(Array.isArray(tr?.data?.data) ? tr.data.data : []);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    getList(pageNumber, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr || fs); setToDate(toStr || ts);
    setDisplayFromDate(new Date(fromStr || fs)); setDisplayToDate(new Date(toStr || ts));
    setPage(1);
    loadAll(1, fromStr || fs, toStr || ts, searchValue, sortField, sortOrder, subscriptionFilter);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setSearchValue(''); setSubscriptionFilter(''); setPreset(null); setPage(1);
    loadAll(1, '', '', '', sortField, sortOrder, '');
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, field, order, subscriptionFilter);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/heart-rate-session-export?from_date=${fromDate}&to_date=${toDate}&search=${searchValue}&sortField=${sortField}&sortOrder=${sortOrder}&subscription_status=${subscriptionFilter}&user_segment=${userSegment}`,
      auth());
    const rows = res.data?.data?.rows || res.data?.data || [];
    // Format dates to match the on-screen columns (FR-020).
    return rows.map((r) => ({ ...r, last_session: r.last_session ? timestampFormatter(r.last_session) : '' }));
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );
  const hasRange = !!(fromDate || toDate || searchValue || subscriptionFilter);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

  const totalSessionsVal = summary.current?.total_sessions ?? totalSessions;
  const totalUsersVal = summary.current?.total_users ?? totalUsers;

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Health › <b>Heart rate sessions</b></span>
            <span className="au-route">/user-heart-rate-sessions</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Heart Rate Sessions</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the heart rate sessions data. Please try again." onRetry={() => loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter)} />
          ) : (
            <>
              {/* Summary before detail */}
              <HeartRateSessionSummaryCards
                totalSessions={totalSessionsVal}
                totalUsers={totalUsersVal}
                totalUserBase={totalUserBase}
                subscribedUserCount={subscribedUserCount}
                deltas={summary.deltas}
              />

              <HeartRateSessionTrendChart data={trend} />

              <HeartRateTopTagsPanel tags={topTags} totalSessions={totalSessionsVal} />

              {/* Filters */}
              <DateRangeBar
                leading={(
                  <>
                    <input className="au-input" type="text" placeholder="Search by name or email" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} aria-label="Search users" />
                    <select className="au-select" name="subscriptionFilter" value={subscriptionFilter} onChange={(e) => setSubscriptionFilter(e.target.value)} aria-label="Subscription filter">
                      <option value="">All Subscriptions</option>
                      <option value="1">Active</option>
                      <option value="2">Cancel</option>
                      <option value="4">Expired</option>
                    </select>
                  </>
                )}
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasRange}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="hr-range-note">
                The date range filters sessions by their <b>activity date (UTC)</b> — the session timestamp shown per user as “Last Session”.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all sessions (no date filter).'}
              </p>

              {/* Detail table */}
              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : users.length === 0 ? (
                <EmptyState title="No sessions in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No users match the current filters.' : 'There are no heart rate sessions to show for this view yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Per-user heart rate sessions</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="heart-rate-sessions.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table au-mau-table hr-table" data-testid="hr-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>Country</th><th>App Version</th><th>Subscription</th>
                          <SortTh field="session_count" label="Session Count" />
                          <SortTh field="total_tag_used" label="Total tag used" />
                          <SortTh field="latest_session_time" label="Last Session" />
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={user.id}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{user.name}</td>
                            <td data-label="Email" className="au-ellipsis">{user.email}</td>
                            <td data-label="Country">{user.country}</td>
                            <td data-label="App Version">{user.mobile_app_version}</td>
                            <td data-label="Subscription">{user.subscription_status}</td>
                            <td data-label="Session Count" className="tnum"><Link className="au-link" to={`/heart-rate-sessions/${btoa(user.id)}`}>{user.session_count}</Link></td>
                            <td data-label="Total tag used" className="tnum">{user.total_tag_used ?? 0}</td>
                            <td data-label="Last Session">{timestampFormatter(user.latest_session_time)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserHeartRateSessionsCount;
